import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import CustomFieldRenderer from '../CustomFieldRenderer';
import CustomFieldBadge from '../custom-fields/CustomFieldBadge';
import { missingRequiredCustomFields } from '../custom-fields/requiredFields';
import CompanyPicker from '../CompanyPicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const ROLES = ['Champion', 'Decision Maker', 'Influencer', 'Blocker', 'User'];
export const SENIORITY = ['C-suite', 'VP', 'Director', 'Manager', 'IC'];

export const EMPTY_CONTACT_FORM = { first_name: '', last_name: '', email: '', phone: '', mobile: '', title: '', department: '', seniority: '', linkedin_url: '', role_in_deal: '', notes: '', account_id: '', custom_fields: {} };

const FULL_FIELDS = [
    { label: 'First Name *', key: 'first_name' }, { label: 'Last Name *', key: 'last_name' },
    { label: 'Personal Email *', key: 'email', type: 'email' }, { label: 'Personal Phone', key: 'phone' },
    { label: 'Mobile/WhatsApp', key: 'mobile' },
    { label: 'Job Title', key: 'title' }, { label: 'Department', key: 'department' },
    { label: 'Personal LinkedIn URL', key: 'linkedin_url' },
];
// The inline create form of the event's Add guests dialog (#1013): the fields a person is asked for at the door.
const COMPACT_FIELDS = [
    { label: 'First Name *', key: 'first_name' }, { label: 'Last Name *', key: 'last_name' },
    { label: 'Email *', key: 'email', type: 'email' }, { label: 'Phone', key: 'phone' },
    { label: 'Job Title', key: 'title' },
];

const LABEL = 'block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1';
const INPUT = 'w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-400';

/**
 * The field block of the New / Edit Contact form (#1013, P5), shared by `ContactModal` and the Guests tab's
 * Add guests dialog, which renders it inline: a contact created from an event is saved with the same fields
 * and rules as one created on the Contacts page, with no second dialog opened on top.
 *
 * The caller owns `form` (and its `setForm`), the duplicate-email notice and Save. `onEmailChange` runs when
 * the email is edited, so the caller can clear that notice. `variant="compact"` drops department, mobile,
 * LinkedIn, seniority, role and notes. `linkedAccount` / `setLinkedAccount` label the company picker with the
 * record already linked to the contact being edited. The company picker's own state (the "Add new company"
 * row) lives here, so it resets when the form unmounts.
 */
export default function ContactFormFields({
    form, setForm, onEmailChange, customFieldDefs = [], showRequired = false,
    linkedAccount = null, setLinkedAccount, variant = 'full',
}) {
    const { getAuthHeader } = useAuth();
    const compact = variant === 'compact';
    const [showNewCompany, setShowNewCompany] = useState(false);
    const [newCompanyName, setNewCompanyName] = useState('');
    const [creatingCompany, setCreatingCompany] = useState(false);
    // Name of the company picked via CompanyPicker during this session (so the trigger shows the picked
    // name immediately, before save).
    const [pickedAccountName, setPickedAccountName] = useState(null);

    const handleCreateCompany = async () => {
        if (!newCompanyName.trim()) return toast.error('Company name required');
        setCreatingCompany(true);
        try {
            const res = await axios.post(`${API}/accounts`, { name: newCompanyName.trim(), status: 'prospect' }, { headers: getAuthHeader() });
            const newAcc = res.data;
            toast.success(`Company "${newAcc.name}" created`);
            setForm(prev => ({ ...prev, account_id: newAcc.account_id }));
            setLinkedAccount?.(newAcc);
            setShowNewCompany(false);
            setNewCompanyName('');
        } catch { toast.error('Failed to create company'); }
        finally { setCreatingCompany(false); }
    };

    const company = (
        <div key="company">
            <label className={LABEL}>Company</label>
            {!showNewCompany ? (
                <>
                    <CompanyPicker
                        value={form.account_id || ''}
                        onChange={(id, name) => {
                            setForm(prev => ({ ...prev, account_id: id }));
                            setPickedAccountName(name || null);
                        }}
                        selectedLabel={pickedAccountName ?? linkedAccount?.name}
                        placeholder="No company"
                        apiBase={API}
                        getAuthHeader={getAuthHeader}
                        testId="contact-account-picker"
                    />
                    <button type="button" onClick={() => { setShowNewCompany(true); setNewCompanyName(''); }}
                        className="mt-1.5 text-xs text-stone-500 hover:text-stone-700 flex items-center gap-1"
                        data-testid="add-new-company-btn">
                        <Plus className="w-3 h-3" /> Add new company
                    </button>
                </>
            ) : (
                <div className="flex gap-2" data-testid="new-company-inline">
                    <input type="text" value={newCompanyName} onChange={e => setNewCompanyName(e.target.value)}
                        placeholder="New company name" autoFocus
                        data-testid="new-company-name-input"
                        className="flex-1 px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                    <button type="button" onClick={handleCreateCompany} disabled={creatingCompany}
                        className="px-3 py-2 text-sm bg-stone-900 text-white rounded-lg hover:bg-stone-800 disabled:opacity-50"
                        data-testid="create-company-btn">
                        {creatingCompany ? '...' : 'Create'}
                    </button>
                    <button type="button" onClick={() => setShowNewCompany(false)}
                        className="px-2 py-2 text-sm border border-stone-200 rounded-lg hover:bg-stone-50 text-stone-500">
                        <X className="w-4 h-4" />
                    </button>
                </div>
            )}
        </div>
    );

    return (
        <>
            <div className="grid grid-cols-2 gap-4 mt-2">
                {(compact ? COMPACT_FIELDS : FULL_FIELDS).map(f => (
                    <div key={f.key}>
                        <label className={LABEL} htmlFor={`contact-${f.key}-input`}>{f.label}</label>
                        <input id={`contact-${f.key}-input`} type={f.type || 'text'} value={form[f.key] || ''} onChange={e => { setForm(prev => ({ ...prev, [f.key]: e.target.value })); if (f.key === 'email') onEmailChange?.(); }}
                            data-testid={`contact-${f.key}-input`}
                            className={INPUT} />
                    </div>
                ))}
                {company}
                {!compact && (
                    <>
                        <div>
                            <label className={LABEL}>Seniority</label>
                            <select value={form.seniority || ''} onChange={e => setForm(prev => ({ ...prev, seniority: e.target.value }))} data-testid="contact-seniority-select"
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none">
                                <option value="">Select...</option>
                                {SENIORITY.map(s => <option key={s}>{s}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className={LABEL}>Role in Deal</label>
                            <select value={form.role_in_deal || ''} onChange={e => setForm(prev => ({ ...prev, role_in_deal: e.target.value }))} data-testid="contact-role-select"
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none">
                                <option value="">Select...</option>
                                {ROLES.map(r => <option key={r}>{r}</option>)}
                            </select>
                        </div>
                        <div className="col-span-2">
                            <label className={LABEL}>Notes</label>
                            <textarea value={form.notes || ''} onChange={e => setForm(prev => ({ ...prev, notes: e.target.value }))} rows={2}
                                className={INPUT.replace(' focus:border-stone-400', '')} />
                        </div>
                    </>
                )}
            </div>
            {customFieldDefs.length > 0 && (
                <div className="mt-4 pt-4 border-t border-stone-200">
                    <label className="flex items-center gap-2 text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">
                        Custom Fields <CustomFieldBadge />
                    </label>
                    <CustomFieldRenderer
                        fields={customFieldDefs}
                        values={form.custom_fields || {}}
                        editing={true}
                        errors={showRequired ? missingRequiredCustomFields(customFieldDefs, form.custom_fields) : {}}
                        onChange={(newCustomFields) => setForm(prev => ({ ...prev, custom_fields: newCustomFields }))}
                    />
                </div>
            )}
        </>
    );
}
